import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';

import { map, Observable } from 'rxjs';

import { CustomIcon, SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { OrderStatus } from '@fcom/common-booking';
import { QualtricsSurveys } from '@fcom/common/qualtrics/qualtrics.utils';
import { ConfigService, isPresent, NativeBridgeService, RootPaths, unique } from '@fcom/core';
import {
  FinnairOrder,
  FinnairServiceItem,
  FinnairTotalPricesDetails,
  PaymentTransaction,
  ServiceFlow,
} from '@fcom/dapi/api/models';
import { finShare } from '@fcom/rx';
import { ButtonPosition, ResponsiveImage } from '@fcom/ui-components';
import { LanguageService } from '@fcom/ui-translate';
import {
  getEligibleAllianzInsuranceId,
  getLastPaidPrices,
  getLastPaidServices,
} from '@fcom/common-booking/utils/order.utils';
import { MANAGE_BOOKING_STEPS } from '@fcom/common-booking/manage-booking-config';
import { CustomServiceType } from '@fcom/dapi/interfaces/services.interface';
import { getCarTrawlerClientId } from '@fcom/common/utils/thirdparty.utils';
import { ExpediaHotelOrigin } from '@fcom/common/enums';
import { FragmentService } from '@fcom/common/services';

@Component({
  selector: 'fin-mmb-purchase-success',
  templateUrl: './purchase-success.component.html',
  styleUrls: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ManageBookingPurchaseSuccessComponent implements OnInit {
  private languageService = inject(LanguageService);
  private configService = inject(ConfigService);
  private nativeBridgeService = inject(NativeBridgeService);
  private fragmentService = inject(FragmentService);

  readonly OrderStatus = OrderStatus;
  readonly survey = QualtricsSurveys.MMB_FEEDBACK;
  readonly ctaUrl: string = `/${this.languageService.langValue}/${RootPaths.MANAGE_BOOKING_ROOT}/${MANAGE_BOOKING_STEPS.VIEW.path}`;
  readonly ButtonPosition = ButtonPosition;
  readonly ServiceFlow = ServiceFlow;
  readonly ExpediaHotelOrigin = ExpediaHotelOrigin;
  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly CustomIcon = CustomIcon;

  @Input()
  order$: Observable<FinnairOrder>;
  @Input()
  orderStatus$: Observable<OrderStatus>;
  @Input()
  paymentMethods$: Observable<PaymentTransaction[] | undefined>;

  @Output()
  loadOrderErrorClick: EventEmitter<boolean> = new EventEmitter<boolean>();

  services$: Observable<FinnairServiceItem[]>;
  prices$: Observable<FinnairTotalPricesDetails>;
  openSurvey = false;
  pointsUsedToPay$: Observable<boolean>;
  passengerEmails$: Observable<string[]>;
  showCarTrawlerWidget$: Observable<boolean>;
  showHotelsWidget$: Observable<boolean>;
  carTrawlerClientId: number;
  showTravelInsuranceBanner$: Observable<boolean>;
  allianz24HourBannerImage$: Observable<ResponsiveImage>;

  ngOnInit(): void {
    this.carTrawlerClientId = getCarTrawlerClientId(
      'MMB_ANCILLARY_CONFIRMATION_PAGE',
      this.nativeBridgeService.isInsideNativeWebView
    );
    this.services$ = this.order$.pipe(
      map((order) => getLastPaidServices(order)),
      finShare()
    );

    this.showTravelInsuranceBanner$ = getEligibleAllianzInsuranceId(this.order$, this.configService.cfg).pipe(
      map((insuranceId) => Boolean(insuranceId))
    );

    this.allianz24HourBannerImage$ = this.fragmentService.getCmsPictureFromFragment(
      'MMB.fragments.allianz.bannerImage'
    );

    this.prices$ = this.order$.pipe(
      map((order) => getLastPaidPrices(order)),
      finShare()
    );

    this.pointsUsedToPay$ = this.prices$.pipe(map((prices) => isPresent(prices?.total?.total?.totalPoints)));
    this.passengerEmails$ = this.order$.pipe(
      map((order) =>
        (order?.passengers ?? [])
          .map((p) => p.email)
          .filter(Boolean)
          .filter(unique)
      )
    );

    this.showCarTrawlerWidget$ = this.order$.pipe(
      map(
        (order) =>
          order.eligibilities?.carRental?.find((eligibility) => {
            return eligibility.id === 'JOURNEY';
          })?.isAllowedToUse
      )
    );

    this.showHotelsWidget$ = this.order$.pipe(
      map((order) => {
        if (!this.configService.cfg.enableHotelsMMB) {
          return false;
        }

        return order.eligibilities?.hotel?.find((eligibility) => {
          return eligibility.id === CustomServiceType.JOURNEY;
        })?.isAllowedToUse;
      })
    );
  }

  openModal($event: Event): void {
    $event.stopPropagation();
    this.openSurvey = true;
  }
}
